import type { ColorMode } from '@color';
import { interpDoc, type Doc } from '@doc';
import type { Point } from '@geom';
import type { Elements, NID } from '@graph';
import { clamp, easing, EasingDirection, EasingType, interp } from '@utils';

const DEFAULT_TRANSITION_DURATION = 0.4; // seconds
// const DEFAULT_TRANSITION_DURATION = 2.0; // For debugging

export class TransitionManager {
  startDoc: Doc;
  startSelection: Elements;
  startPan: Point;
  startZoom: number;
  endDoc: Doc;
  endSelection: Elements;
  endPan: Point;
  endZoom: number;
  duration: number;
  startDate: Date;
  interpDoc: (t: number) => Doc;
  interpSelection: (t: number) => Elements;
  interpZoom: (t: number) => number;
  interpPan: (t: number) => Point;
  easing: (t: number) => number;

  constructor(
    startDoc: Doc,
    startSelection: Elements,
    startPan: Point,
    startZoom: number,
    endDoc: Doc,
    endSelection: Elements,
    endPan: Point,
    endZoom: number,
    colorMode: ColorMode,
    entryPoints: Map<NID, Point> | undefined,
    duration: number = DEFAULT_TRANSITION_DURATION
  ) {
    this.startDoc = startDoc;
    this.startSelection = startSelection;
    this.startPan = startPan;
    this.startZoom = startZoom;
    this.endDoc = endDoc;
    this.endSelection = endSelection;
    this.endPan = endPan;
    this.endZoom = endZoom;
    this.duration = duration;
    this.startDate = new Date();
    this.interpDoc = interpDoc(startDoc, endDoc, colorMode, { entryPoints });
    this.interpSelection = startSelection.interp(endSelection);
    this.interpZoom = interp([startZoom, endZoom]);
    this.interpPan = startPan.interp(endPan);
    this.easing = easing(EasingDirection.IN_OUT, EasingType.QUADRATIC);
  }

  private elapsed(): number {
    const now = new Date();
    return (now.getTime() - this.startDate.getTime()) / 1000;
  }

  getAt(t: number): {
    doc: Doc;
    selection: Elements;
    pan: Point;
    zoom: number;
  } {
    const result = {
      doc: this.interpDoc(t),
      selection: this.interpSelection(t),
      pan: this.interpPan(t),
      zoom: this.interpZoom(t)
    };
    return result;
  }

  get(): {
    doc: Doc;
    selection: Elements;
    pan: Point;
    zoom: number;
    t: number;
  } {
    const elapsed = this.elapsed();
    // console.log(`elapsed: ${elapsed}`);

    const t = this.easing(clamp(elapsed / this.duration));
    return { ...this.getAt(t), t };
  }
}
